// Auto-estimate a song's difficulty from its chord list + BPM.
// Returns one of: 'beginner' | 'intermediate' | 'advanced' | 'pro' with a score
// and a short French explanation of the main factors.
import { parseChord } from '@/lib/pianoChords';

// Basic open-position chords a beginner learns first.
const BEGINNER_OPEN = new Set([
  'C', 'D', 'E', 'G', 'A', 'Am', 'Em', 'Dm', 'D7', 'E7', 'A7', 'G7',
]);
// Common barre chords / mildly tricky
const BARRE_CHORDS = new Set(['F', 'B', 'Bm', 'Bb', 'F#m', 'C#m']);

const CHORD_TOKEN = /\b([A-G](?:#|b)?(?:maj|min|m|dim|aug|sus)?(?:\d{1,2})?(?:sus\d)?(?:add\d)?(?:b\d)?(?:\/[A-G](?:#|b)?)?)\b/g;

function scoreChord(raw) {
  const trimmed = raw.trim();
  const isSlash = trimmed.includes('/');
  const base = isSlash ? trimmed.split('/')[0] : trimmed;

  // Recognise pattern
  const p = parseChord(trimmed);
  if (!p) return 0; // unknown token — ignore

  let s = 0;
  if (BEGINNER_OPEN.has(base)) s = 0;
  else if (BARRE_CHORDS.has(base)) s = 2;
  else if (/^[A-G](#|b)?7$/.test(base)) s = 1;       // simple 7th chord
  else if (/(maj7|m7)$/.test(base)) s = 2;           // extended
  else if (/(m7b5|dim7?|aug|sus[24]?|add\d|9|11|13|b[59]|#[59])/.test(base)) s = 4; // complex
  else s = 1; // any other minor / major variant

  if (isSlash) s += 1;
  return s;
}

/**
 * Estimate difficulty from a chord chart text + BPM.
 * @param {string} chordsText — free-form chords+lyrics text
 * @param {number} bpm — tempo
 * @returns { level, score, reasons: string[], distinctChords: number }
 */
export function estimateDifficulty(chordsText, bpm) {
  const tokens = new Set();
  for (const m of (chordsText || '').matchAll(CHORD_TOKEN)) {
    const t = m[1];
    if (parseChord(t)) tokens.add(t);
  }
  const distinct = tokens.size;
  const chordScores = Array.from(tokens).map(scoreChord);
  const chordSum = chordScores.reduce((s, v) => s + v, 0);
  const avgChord = distinct > 0 ? chordSum / distinct : 0;

  let score = 0;
  score += avgChord * 2;                             // chord complexity weight
  score += Math.max(0, distinct - 4) * 0.4;          // penalise long unique lists

  const b = Number(bpm) || 0;
  if (b > 0) {
    if (b < 70) score += 0;
    else if (b < 100) score += 0.5;
    else if (b < 130) score += 1.2;
    else if (b < 160) score += 2.2;
    else score += 3.2;
  }

  let level = 'beginner';
  if (score >= 12) level = 'pro';
  else if (score >= 7) level = 'advanced';
  else if (score >= 3) level = 'intermediate';

  const reasons = [];
  if (distinct === 0) return { level: null, score: 0, reasons: ['Aucun accord détecté'], distinctChords: 0 };
  reasons.push(`${distinct} accord${distinct > 1 ? 's' : ''} unique${distinct > 1 ? 's' : ''}`);
  const barres = Array.from(tokens).filter(t => BARRE_CHORDS.has(t.split('/')[0]));
  if (barres.length) reasons.push(`${barres.length} barré${barres.length > 1 ? 's' : ''}`);
  const complex = Array.from(tokens).filter(t => scoreChord(t) >= 4);
  if (complex.length) reasons.push(`${complex.length} accord${complex.length > 1 ? 's' : ''} complexe${complex.length > 1 ? 's' : ''}`);
  if (b >= 130) reasons.push(`tempo rapide (${b} BPM)`);
  else if (b > 0 && b < 80) reasons.push(`tempo lent (${b} BPM)`);

  return { level, score: Math.round(score * 10) / 10, reasons, distinctChords: distinct };
}

export const DIFFICULTY_LABEL_FR = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  advanced: 'Avancé',
  pro: 'Pro',
};

export const DIFFICULTY_ORDER = ['beginner', 'intermediate', 'advanced', 'pro'];

export function difficultyDelta(estimated, stored) {
  if (!estimated || !stored) return 0;
  return DIFFICULTY_ORDER.indexOf(estimated) - DIFFICULTY_ORDER.indexOf(stored);
}
